import { useState } from 'react';
import type { HotStreakState, BetSide, BettingTicket, MascotId } from '@flip7/shared';
import { HS_BETTING_TIMER_MS, HS_MASCOTS, HS_MASCOT_IDS } from '@flip7/shared';
import { useCountdown } from './useCountdown';
import { useHotStreakTips } from '../../hooks/useHotStreakTips';
import { Tooltip } from './Tooltip';

const MASCOT_EMOJI: Record<MascotId, string> = {
  hurley: '🌭', gobbler: '🐻', dangle: '🐟', mum: '👑',
};

interface Props {
  state: HotStreakState;
  playerId: string | null;
  onDraftPick: (ticketId: string, side: BetSide) => void;
}

export function HotStreakBetting({ state, playerId, onDraftPick }: Props) {
  const [selectedSide, setSelectedSide] = useState<BetSide>('safe');
  const tips = useHotStreakTips();
  const currentDrafterId = state.draftOrder[state.currentDrafterIndex];
  const isMyTurn = currentDrafterId === playerId;
  const currentDrafter = state.players.find((p) => p.id === currentDrafterId);
  const timeLeft = useCountdown(state.draftTimerStartedAt, HS_BETTING_TIMER_MS);

  const mascotTickets = state.availableTickets.filter((t) => t.type === 'mascot');
  const sideTickets = state.availableTickets.filter((t) => t.type === 'side');

  // Group mascot tickets by mascot, then find the top available tier per mascot
  const topTierByMascot = new Map<string, number>();
  for (const mascotId of HS_MASCOT_IDS) {
    const tiers = mascotTickets
      .filter((t) => t.mascot === mascotId)
      .map((t) => t.tier)
      .sort((a, b) => a - b);
    if (tiers.length > 0) {
      topTierByMascot.set(mascotId, tiers[0]);
    }
  }

  // A ticket is pickable only if it's the top tier for its mascot
  const isPickable = (ticket: BettingTicket) => {
    if (ticket.type !== 'mascot' || !ticket.mascot) return true;
    return ticket.tier === topTierByMascot.get(ticket.mascot);
  };

  const handlePick = (ticket: BettingTicket) => {
    if (!isMyTurn || !isPickable(ticket)) return;
    onDraftPick(ticket.id, selectedSide);
  };

  return (
    <div className="hs-betting">
      <div className="hs-betting-header">
        <h2>Betting Round</h2>
        <div className="hs-draft-info">
          <span className={`hs-drafter ${isMyTurn ? 'hs-drafter-you' : ''}`}>
            {isMyTurn ? 'Your pick!' : `${currentDrafter?.name}'s pick`}
          </span>
          <span className="hs-timer">{Math.ceil(timeLeft / 1000)}s</span>
        </div>
      </div>

      {/* Draft order indicator */}
      <div className="hs-draft-order">
        {state.draftOrder.map((pid, i) => {
          const player = state.players.find((p) => p.id === pid);
          return (
            <span
              key={pid}
              className={`hs-draft-dot ${i === state.currentDrafterIndex ? 'active' : ''} ${pid === playerId ? 'you' : ''}`}
            >
              {player?.name?.charAt(0) ?? '?'}
            </span>
          );
        })}
        <span className="hs-draft-direction">
          {state.draftDirection === 'forward' ? '→' : '←'}
        </span>
      </div>

      {/* Side toggle */}
      {isMyTurn && (
        <div className="hs-side-toggle">
          <button
            className={`hs-side-btn ${selectedSide === 'safe' ? 'active' : ''}`}
            onClick={() => setSelectedSide('safe')}
          >
            Safe
          </button>
          <button
            className={`hs-side-btn hs-side-risky ${selectedSide === 'risky' ? 'active' : ''}`}
            onClick={() => setSelectedSide('risky')}
          >
            Risky!
          </button>
        </div>
      )}

      {/* Mascot bet tickets — grouped by mascot, stacked by tier */}
      <div className="hs-tickets-section">
        <h3>Mascot Bets</h3>
        <div className="hs-mascot-stacks">
          {HS_MASCOT_IDS.map((mascotId) => {
            const info = HS_MASCOTS[mascotId];
            const tickets = mascotTickets
              .filter((t) => t.mascot === mascotId)
              .sort((a, b) => a.tier - b.tier);
            if (tickets.length === 0) return (
              <div key={mascotId} className="hs-mascot-stack hs-stack-empty">
                <div className="hs-stack-header" style={{ color: info.color }}>
                  <span>{MASCOT_EMOJI[mascotId]}</span> {info.name}
                </div>
                <div className="hs-stack-sold-out">All taken</div>
              </div>
            );

            const topTicket = tickets[0];
            const payout = selectedSide === 'safe' ? topTicket.safePayout : topTicket.riskyPayout;
            const tp = payout as { first: number; second: number; third: number };
            const canPick = isMyTurn && isPickable(topTicket);

            return (
              <div key={mascotId} className="hs-mascot-stack">
                <div className="hs-stack-header" style={{ color: info.color }}>
                  <span>{MASCOT_EMOJI[mascotId]}</span> {info.name}
                </div>
                {/* Stacked cards behind */}
                {tickets.length > 1 && (
                  <div className="hs-stack-behind">
                    {tickets.slice(1).map((t) => (
                      <div key={t.id} className="hs-stack-card-back" />
                    ))}
                  </div>
                )}
                {/* Top pickable ticket */}
                <button
                  className={`hs-stack-ticket ${canPick ? 'hs-stack-pickable' : ''}`}
                  style={{ borderColor: info.color }}
                  disabled={!canPick}
                  onClick={() => handlePick(topTicket)}
                >
                  <div className="hs-stack-payouts">
                    <span>🥇 ${tp.first}</span>
                    <span>🥈 ${tp.second}</span>
                    <span>🥉 ${tp.third}</span>
                  </div>
                  <div className="hs-stack-tier">Tier {topTicket.tier} of {tickets.length + (3 - tickets.length - topTicket.tier + 1)}</div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Side bet tickets */}
      {sideTickets.length > 0 && (
        <div className="hs-tickets-section">
          <h3>Side Bets</h3>
          <div className="hs-tickets-grid">
            {sideTickets.map((ticket) => {
              const payout = selectedSide === 'safe' ? ticket.safePayout : ticket.riskyPayout;
              const sp = payout as { correct: number; incorrect: number };
              return (
                <button
                  key={ticket.id}
                  className="hs-ticket hs-ticket-side"
                  disabled={!isMyTurn}
                  onClick={() => handlePick(ticket)}
                >
                  <div className="hs-ticket-question">{ticket.question}</div>
                  <div className="hs-ticket-payouts">
                    <span className="hs-payout hs-payout-yes">Yes: ${sp.correct}</span>
                    <span className="hs-payout hs-payout-no">No: ${sp.incorrect}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* My bets so far */}
      {playerId && (
        <div className="hs-my-bets">
          <h3>Your Bets</h3>
          {state.players.find((p) => p.id === playerId)?.bets.map((bet, i) => (
            <div key={i} className="hs-my-bet">
              <span>
                {bet.ticket.mascot && MASCOT_EMOJI[bet.ticket.mascot]}{' '}
                {bet.ticket.mascot ? HS_MASCOTS[bet.ticket.mascot]?.name : bet.ticket.question}
              </span>
              <span className={`hs-bet-side ${bet.side}`}>{bet.side}</span>
            </div>
          )) ?? <p>No bets yet</p>}
        </div>
      )}

      {/* First-time tooltip */}
      {tips.shouldShow('bet-intro') && (
        <Tooltip
          icon="🎫"
          message="Pick a mascot you think will win! You must take the top ticket from each stack. Choose Safe for steady payouts, or Risky for bigger wins."
          onDismiss={() => tips.dismiss('bet-intro')}
        />
      )}
    </div>
  );
}
